// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { Button } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUI } from "../../contexts/ui";
import {
  type ReviewCommentGroup,
  reviewCommentGroup,
  type ReviewCommentVisibilityFields,
} from "../../utils/visibleReviewComments";

const MOVED_TEST_ID = "ask-v2-review-comment-moved";

/**
 * Says a love or a reject moved a finding out of Unresolved, on the snackbar
 * the rest of the app already uses.
 *
 * why the verdict on the comment, not a click: the mutation writes `liked`
 * before the next read, and a failed write never does, so the notice and the
 * section change on the same update.
 */
export function useReviewCommentMoveNotice(
  comments: readonly (ReviewCommentVisibilityFields & { _id: string })[],
  onUndo?: (commentId: string) => void,
  /** Ids a bulk verdict is writing: each move is skipped once, as the bar sums them up. */
  quiet?: { readonly current: Set<string> },
): void {
  const { t } = useTranslation("ui");
  const { notify } = useUI();
  const previous = useRef<Map<string, ReviewCommentGroup> | null>(null);

  useEffect(() => {
    const current = new Map(
      comments.map((comment) => [comment._id, reviewCommentGroup(comment)]),
    );
    const prior = previous.current;
    previous.current = current;
    if (prior == null || typeof notify !== "function") return;

    for (const comment of comments) {
      const group = current.get(comment._id);
      if (group == null || prior.get(comment._id) !== "unresolved") continue;
      const key =
        group === "confirmed"
          ? "askV2.review.movedToApproved"
          : group === "rejected"
            ? "askV2.review.movedToRejected"
            : null;
      if (key == null) continue;
      // why: one toast per comment of a bulk verdict queues N eight-second
      // toasts whose Undo no longer names its comment.
      if (quiet?.current.delete(comment._id)) continue;
      const text = t(key);
      // why: the shared snackbar sets aria-hidden, which drops the toast from the accessibility tree, and its prop type rejects data-testid on an object literal even though the aside spreads it.
      notify({
        title: text,
        variant: group === "confirmed" ? "success" : "default",
        "aria-label": text,
        "aria-hidden": false,
        "data-testid": MOVED_TEST_ID,
        ...(onUndo
          ? {
              // why: long enough to reach the button; the default 3s is not.
              timeout: UNDO_TIMEOUT_MS,
              actionButtons: (
                <UndoMoveButton
                  label={t("askV2.review.undoStatus")}
                  onUndo={() => onUndo(comment._id)}
                />
              ),
            }
          : {}),
      } as Parameters<typeof notify>[0]);
    }
  }, [comments, notify, onUndo, quiet, t]);
}

const UNDO_TIMEOUT_MS = 8000;

/** Undo once: the toast stays up until it times out, so a second click would re-write the status. */
function UndoMoveButton({
  label,
  onUndo,
}: {
  label: string;
  onUndo: () => void;
}) {
  const [done, setDone] = useState(false);
  return (
    <Button
      type="button"
      size="1"
      variant="soft"
      disabled={done}
      data-testid="ask-v2-review-comment-moved-undo"
      onClick={() => {
        setDone(true);
        onUndo();
      }}
    >
      {label}
    </Button>
  );
}
